import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2, Lock, RefreshCw, Cloud, CloudUpload, Bot, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Vault() {
  const [uploadingId, setUploadingId] = useState(null);
  const [aiEditId, setAiEditId] = useState(null);

  const toggleAiSendable = async (p) => {
    if (!p.fanvue_media_id && !p.ai_sendable) {
      toast.error("Fotku najprv nahraj do Fanvue (☁️) — AI vie posielať len fotky z Fanvue Vaultu");
      return;
    }
    try {
      await api.patch(`/photos/${p.id}`, { ai_sendable: !p.ai_sendable });
      load();
      toast.success(!p.ai_sendable ? "AI ju smie ponúkať 🤖" : "AI ju už nebude ponúkať");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zmena zlyhala");
    }
  };

  const saveAiMeta = async (p, ai_desc, ai_level, ai_free, price, category) => {
    const patch = {
      ai_desc, ai_level, ai_free,
      category: (category || "").trim() || null,
      price: ai_free ? 0 : (parseFloat(price) || 0),
    };
    try {
      const r = await api.patch(`/photos/${p.id}`, patch);
      // Over, že DB naozaj uložila to, čo sme poslali (odhalí chýbajúci stĺpec / RLS)
      const saved = r?.data ?? {};
      const mismatch = [];
      if (String(saved.ai_desc ?? "") !== String(ai_desc ?? "")) mismatch.push("popis");
      if (String(saved.ai_level ?? "") !== String(ai_level)) mismatch.push("úroveň");
      if (!!saved.ai_free !== !!ai_free) mismatch.push("free");
      if (Number(saved.price ?? 0) !== Number(patch.price)) mismatch.push("cena");
      if (mismatch.length) {
        toast.error(`DB neuložila: ${mismatch.join(", ")} — chýba stĺpec alebo blokuje RLS. Spusti SQL migrácie.`, { duration: 10000 });
      } else {
        toast.success("Uložené ✓");
      }
      setAiEditId(null);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || "Uloženie zlyhalo";
      toast.error(`Uloženie zlyhalo: ${d}`, { duration: 10000 });
    }
  };
  const uploadToFanvue = async (p) => {
    setUploadingId(p.id);
    try {
      const r = await api.post("/photos/upload-to-fanvue", { photo_id: p.id });
      toast.success(r.data?.status === "processing"
        ? "Nahrané do Fanvue Vaultu — spracováva sa ✨"
        : "Nahrané do Fanvue ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fanvue upload zlyhal");
    } finally { setUploadingId(null); }
  };
  const { t } = useT();
  const [photos, setPhotos] = useState([]);
  const [fans, setFans] = useState([]);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const [p, f] = await Promise.all([api.get("/photos"), api.get("/fans")]);
    setPhotos(p.data);
    setFans(f.data);
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    await api.delete(`/photos/${id}`);
    toast.success("Removed");
    load();
  };

  const pullFromFanvue = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/photos/sync-from-fanvue");
      toast.success(`Pulled ${r.data.imported} from Fanvue (${r.data.skipped} skipped)`, { duration: 6000 });
      load();
    } catch (e) {
      toast.error("Pull failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const refreshUrls = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/photos/refresh-fanvue-urls");
      toast.success(`Refreshed ${r.data.refreshed}/${r.data.total} URLs`, { duration: 5000 });
      load();
    } catch (e) {
      toast.error("Refresh failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 fade-up">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-white/40">{t("vault")}</div>
          <h1 className="font-serif text-5xl mt-2">Photo vault</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={refreshUrls}
            disabled={refreshing}
            data-testid="btn-refresh-urls"
            variant="outline"
            className="bg-transparent border-white/15 text-white/70 hover:bg-white/5 rounded-none px-4 text-xs uppercase tracking-widest"
            title="Refresh expired Fanvue image URLs"
          >
            <RefreshCw size={13} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "..." : "Refresh URLs"}
          </Button>
          <Button
            onClick={pullFromFanvue}
            disabled={syncing}
            data-testid="btn-pull-fanvue-photos"
            variant="outline"
            className="bg-transparent border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 rounded-none px-5 text-xs uppercase tracking-widest"
          >
            <Cloud size={14} className={`mr-2 ${syncing ? "animate-pulse" : ""}`} />
            {syncing ? "Pulling…" : "Pull from Fanvue"}
          </Button>
          <UploadDialog open={open} setOpen={setOpen} onDone={load} />
        </div>
      </div>

      {photos.length === 0 ? (
        <div className="border border-white/5 px-6 py-16 text-center text-white/40 font-serif italic">
          Empty vault. Upload your first photo to sell or tease 💜
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div key={p.id} className="group border border-white/5 bg-[rgb(18,20,28)] overflow-hidden" data-testid={`photo-${p.id}`}>
              <div className="aspect-square overflow-hidden relative">
                <img
                  src={p.image_url || p.image_base64}
                  alt={p.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect fill='%23222' width='100' height='100'/><text x='50' y='55' text-anchor='middle' fill='%23666' font-size='8'>image expired</text></svg>"; }}
                />
                {p.nsfw && (
                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest text-rose-400 bg-black/60 px-2 py-1 flex items-center gap-1">
                    <Lock size={9} /> nsfw
                  </span>
                )}
                {!p.ai_free && p.ai_sendable && Number(p.price) > 0 && (
                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest text-fuchsia-300 bg-black/70 px-2 py-1">
                    ${Number(p.price).toFixed(0)}
                  </span>
                )}
                {p.ai_free && p.ai_sendable && (
                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest text-emerald-300 bg-black/70 px-2 py-1 flex items-center gap-1">
                    <Gift size={9} /> free
                  </span>
                )}
                {p.source === "fanvue" && (
                  <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest text-emerald-300 bg-black/60 px-2 py-1 flex items-center gap-1">
                    <Cloud size={9} /> fanvue
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-serif text-base truncate">{p.title}</div>
                  <div className="font-serif text-lg text-emerald-400 tabular-nums">${p.price.toFixed(2)}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => toggleAiSendable(p)} data-testid={`btn-ai-${p.id}`}
                    title={p.ai_sendable ? "AI ju smie ponúkať — klikni pre vypnutie" : "Povoliť AI ponúkať túto fotku"}
                    className={`transition-colors ${p.ai_sendable ? "text-fuchsia-400" : "text-white/30 hover:text-white/60"}`}>
                    <Bot size={13} />
                  </button>
                  {p.ai_sendable && (
                    <button onClick={() => setAiEditId(aiEditId === p.id ? null : p.id)}
                      data-testid={`btn-aimeta-${p.id}`} title="Popis pre AI + úroveň"
                      className="text-white/30 hover:text-white/60 text-[10px] leading-none">⚙</button>
                  )}
                  {!p.fanvue_media_id && (
                    <button onClick={() => uploadToFanvue(p)} disabled={uploadingId === p.id}
                      data-testid={`btn-tofanvue-${p.id}`} title="Upload to Fanvue Vault"
                      className="text-emerald-300/70 hover:text-emerald-300 disabled:opacity-40 transition-colors">
                      <CloudUpload size={13} className={uploadingId === p.id ? "animate-pulse" : ""} />
                    </button>
                  )}
                  <OfferDialog photo={p} fans={fans} />
                  <button
                    onClick={() => remove(p.id)}
                    className="text-white/30 hover:text-rose-400 p-1"
                    data-testid={`btn-delete-${p.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {aiEditId === p.id && (
                <AiMetaPanel photo={p} onSave={(d, l, f, pr, c) => saveAiMeta(p, d, l, f, pr, c)} onClose={() => setAiEditId(null)} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadDialog({ open, setOpen, onDone }) {
  const { t } = useT();
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("9.99");
  const [nsfw, setNsfw] = useState(false);
  const [b64, setB64] = useState(null);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setB64(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!b64 || !title) {
      toast.error("Title + image required");
      return;
    }
    await api.post("/photos", {
      title,
      image_base64: b64,
      price: parseFloat(price) || 0,
      nsfw,
      tags: [],
    });
    setOpen(false);
    setTitle("");
    setPrice("9.99");
    setNsfw(false);
    setB64(null);
    toast.success("Uploaded");
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="btn-add-photo" className="bg-white text-black hover:bg-white/90 rounded-none px-5 text-xs uppercase tracking-widest">
          <Upload size={14} className="mr-2" /> {t("add_photo")}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[rgb(18,20,28)] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("add_photo")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/40">{t("title")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="input-photo-title" className="bg-transparent border-white/10 rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/40">{t("price")} ($)</Label>
            <Input value={price} onChange={(e) => setPrice(e.target.value)} data-testid="input-photo-price" className="bg-transparent border-white/10 rounded-none mt-1" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="nsfw" checked={nsfw} onChange={(e) => setNsfw(e.target.checked)} data-testid="input-photo-nsfw" />
            <Label htmlFor="nsfw" className="text-xs uppercase tracking-widest text-white/70">{t("nsfw")}</Label>
          </div>
          <div>
            <input type="file" accept="image/*" onChange={onFile} ref={fileRef} data-testid="input-photo-file" className="block w-full text-xs text-white/70 file:bg-fuchsia-500/20 file:border-0 file:text-fuchsia-300 file:px-3 file:py-2 file:mr-3 file:text-xs file:uppercase file:tracking-widest" />
          </div>
          {b64 && <img src={b64} alt="" className="max-h-40 object-contain mx-auto" />}
          <Button onClick={submit} data-testid="btn-upload-photo" className="w-full bg-white text-black hover:bg-white/90 rounded-none text-xs uppercase tracking-widest py-5">
            {t("upload")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OfferDialog({ photo, fans }) {
  const [open, setOpen] = useState(false);
  const [fanId, setFanId] = useState("");
  const [price, setPrice] = useState(photo.price.toString());
  const [teaser, setTeaser] = useState("");

  const submit = async () => {
    if (!fanId) {
      toast.error("Pick a fan");
      return;
    }
    await api.post("/photos/offer", {
      fan_id: fanId,
      photo_id: photo.id,
      custom_price: parseFloat(price) || undefined,
      teaser: teaser || undefined,
    });
    toast.success("Offered");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-[9px] uppercase tracking-widest text-fuchsia-300 hover:text-fuchsia-200" data-testid={`btn-offer-${photo.id}`}>
          offer
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[rgb(18,20,28)] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Offer "{photo.title}"</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/40">Fan</Label>
            <Select value={fanId} onValueChange={setFanId}>
              <SelectTrigger className="bg-transparent border-white/10 rounded-none mt-1" data-testid="select-offer-fan">
                <SelectValue placeholder="Select fan" />
              </SelectTrigger>
              <SelectContent className="bg-[rgb(18,20,28)] border-white/10">
                {fans.map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`option-fan-${f.id}`}>
                    {f.platform.toUpperCase()} · {f.name} · ${(f.spend_total || 0).toFixed(0)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/40">Price ($)</Label>
            <Input value={price} onChange={(e) => setPrice(e.target.value)} className="bg-transparent border-white/10 rounded-none mt-1" data-testid="input-offer-price" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/40">Custom teaser (optional)</Label>
            <Input value={teaser} onChange={(e) => setTeaser(e.target.value)} placeholder="leave blank for AI flirty default" className="bg-transparent border-white/10 rounded-none mt-1" data-testid="input-offer-teaser" />
          </div>
          <Button onClick={submit} data-testid="btn-send-offer" className="w-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white rounded-none text-xs uppercase tracking-widest py-5">
            send offer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Popis fotky pre AI + úroveň (AI podľa toho vyberá) ──
function AiMetaPanel({ photo, onSave, onClose }) {
  const [desc, setDesc] = useState(photo.ai_desc || "");
  const [level, setLevel] = useState(photo.ai_level || "soft");
  const [free, setFree] = useState(!!photo.ai_free);
  const [price, setPrice] = useState(String(photo.price ?? ""));
  const [category, setCategory] = useState(photo.category || "");
  return (
    <div className="px-3 pb-3 border-t border-white/5 pt-3 space-y-2" data-testid={`aimeta-${photo.id}`}>
      <input
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        placeholder="Trieda / kategória (napr. lingerie, gym, shower)"
        data-testid={`aicat-${photo.id}`}
        className="w-full bg-black/40 border border-white/10 px-2 py-1.5 text-xs outline-none focus:border-fuchsia-500/50 placeholder:text-white/25"
      />
      <input
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Popis pre AI (napr. červené prádlo, ležérna póza)"
        data-testid={`aidesc-${photo.id}`}
        className="w-full bg-black/40 border border-white/10 px-2 py-1.5 text-xs outline-none focus:border-fuchsia-500/50 placeholder:text-white/25"
      />
      <div className="flex gap-1">
        {["teasing", "soft", "explicit"].map((l) => (
          <button
            key={l}
            onClick={() => setLevel(l)}
            data-testid={`ailevel-${l}-${photo.id}`}
            className={`flex-1 text-[9px] uppercase tracking-widest py-1.5 border transition-colors ${
              level === l
                ? "border-fuchsia-500 text-fuchsia-300 bg-fuchsia-500/10"
                : "border-white/10 text-white/40 hover:text-white/70"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className={free ? "opacity-40 pointer-events-none" : ""}>
        <label className="text-[9px] uppercase tracking-widest text-white/40">Cena PPV ($) — 0 = AI zvolí sama</label>
        <input
          type="number" min="0" step="1" value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="napr. 15"
          data-testid={`aiprice-${photo.id}`}
          className="w-full bg-black/40 border border-white/10 px-2 py-1.5 text-xs outline-none focus:border-fuchsia-500/50 mt-1 placeholder:text-white/25"
        />
      </div>
      <button
        onClick={() => setFree(!free)}
        data-testid={`aifree-${photo.id}`}
        className={`w-full flex items-center justify-center gap-2 text-[9px] uppercase tracking-widest py-1.5 border transition-colors ${
          free
            ? "border-emerald-500 text-emerald-300 bg-emerald-500/10"
            : "border-white/10 text-white/40 hover:text-white/70"
        }`}
      >
        <Gift size={11} /> {free ? "AI ju smie dať zadarmo" : "Len platená (PPV)"}
      </button>
      <div className="flex gap-2">
        <button onClick={() => onSave(desc, level, free, price, category)} data-testid={`aisave-${photo.id}`}
          className="flex-1 text-[10px] uppercase tracking-widest py-1.5 bg-fuchsia-500/20 text-fuchsia-200 hover:bg-fuchsia-500/30">
          Uložiť
        </button>
        <button onClick={onClose}
          className="px-3 text-[10px] uppercase tracking-widest py-1.5 text-white/40 hover:text-white">
          ✕
        </button>
      </div>
    </div>
  );
}
